import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2,
  Users,
  Palette,
  ToggleRight,
  BookOpen,
  BarChart3,
  Plus,
  Search,
  ChevronDown,
  CheckCircle,
  XCircle,
  Loader2,
  Upload,
  RefreshCw,
  Send,
  Shield,
  TrendingUp,
  Activity,
  UserPlus,
  FileSpreadsheet,
  Download,
  GraduationCap,
  ExternalLink,
  Rocket,
  Trash2,
  Pencil,
  AlertTriangle,
  HelpCircle,
  Server,
  Globe,
  Settings,
  ArrowRight,
  Copy,
  Check,
  Eye,
  EyeOff,
  Key
} from 'lucide-react';
import TenantFeatureManagement from './TenantFeatureManagement';
import BrandingSettings from './BrandingSettings';
import {
  tenantSignInHosts,
  redirectUris,
  dnsRecords,
  ssoLoginPath,
  ssoConfigExample,
  detectAppServiceHost,
  detectVerificationId,
  storeSetupValue,
  APP_SERVICE_HOST_KEY,
  VERIFICATION_ID_KEY
} from '../../utils/tenantSetup';
import { pickUnpublishTarget } from '../../utils/tenantUnpublish';
import { API } from '../../config/api';

export default function UnifiedTenantManagement() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Data states
  const [organizations, setOrganizations] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [publishedContent, setPublishedContent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  
  // Modal states
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const orgs = response.data.organizations || [];
      setOrganizations(orgs);
      
      // Auto-select first tenant if none selected
      if (!selectedTenant && orgs.length > 0) {
        setSelectedTenant(orgs[0]);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, [token, selectedTenant]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [token]);

  // Fetch published content
  const fetchPublishedContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/content/published`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublishedContent(response.data.published_content);
    } catch (error) {
      console.error('Error fetching published content:', error);
    }
  }, [token]);

  // Fetch all courses
  const fetchCourses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, [token]);

  // Fetch users for selected tenant
  const fetchTenantUsers = useCallback(async () => {
    if (!selectedTenant?.org_id) return;
    try {
      const response = await axios.get(`${API}/organizations/${selectedTenant.org_id}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      setTenantUsers([]);
    }
  }, [token, selectedTenant]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOrganizations(),
        fetchAnalytics(),
        fetchPublishedContent(),
        fetchCourses()
      ]);
      setLoading(false);
    };
    
    if (token) {
      loadData();
    }
  }, [token, fetchOrganizations, fetchAnalytics, fetchPublishedContent, fetchCourses]);

  // Fetch tenant users when tenant changes
  useEffect(() => {
    if (selectedTenant) {
      fetchTenantUsers();
    }
  }, [selectedTenant, fetchTenantUsers]);

  const tabs = [
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'features', label: 'Features', icon: ToggleRight },
    { id: 'content', label: 'Content', icon: BookOpen },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'setup-guide', label: 'Setup Guide', icon: HelpCircle },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  // Check if user is super admin
  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
        <p className="text-zinc-400">Super admin access required to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="unified-tenant-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-7 w-7 text-navigate-gold" />
            Tenant Management
          </h1>
          <p className="text-zinc-400 mt-1">
            Configure tenants, users, branding, features, and content publishing
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateTenant(true)} 
          className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          data-testid="create-tenant-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Tenant
        </Button>
      </div>

      {/* Tenant Selector - Always visible except on Tenants tab */}
      {activeTab !== 'tenants' && organizations.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label className="text-zinc-400 text-sm">Configuring:</Label>
              <div className="relative">
                <button
                  onClick={() => setShowTenantSelector(!showTenantSelector)}
                  className="flex items-center gap-3 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                  data-testid="tenant-selector-btn"
                >
                  {selectedTenant?.logo_url ? (
                    <img src={selectedTenant.logo_url} alt="" className="w-6 h-6 rounded object-contain" />
                  ) : (
                    <div className="w-6 h-6 rounded bg-navigate-gold/20 flex items-center justify-center">
                      <Building2 className="h-3 w-3 text-navigate-gold" />
                    </div>
                  )}
                  <span className="text-white font-medium">{selectedTenant?.name || 'Select Tenant'}</span>
                  <ChevronDown className="h-4 w-4 text-zinc-400" />
                </button>
                
                {showTenantSelector && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    {organizations.map(org => (
                      <button
                        key={org.org_id}
                        onClick={() => {
                          setSelectedTenant(org);
                          setShowTenantSelector(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left ${
                          selectedTenant?.org_id === org.org_id ? 'bg-zinc-800' : ''
                        }`}
                      >
                        {org.logo_url ? (
                          <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-contain" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-zinc-700 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-zinc-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{org.name}</p>
                          <p className="text-xs text-zinc-500">{org.slug}.cerebro.co.za</p>
                        </div>
                        {selectedTenant?.org_id === org.org_id && (
                          <CheckCircle className="h-4 w-4 text-navigate-gold ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {selectedTenant && (
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {selectedTenant.current_users || 0} / {selectedTenant.max_users} users
                </span>
                <span className={`flex items-center gap-1 ${selectedTenant.is_active ? 'text-green-400' : 'text-red-400'}`}>
                  {selectedTenant.is_active ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {selectedTenant.is_active ? 'Active' : 'Inactive'}
                </span>
                {/* Launch Button - Preview tenant login */}
                {selectedTenant && (
                  <a
                    href={selectedTenant.custom_domain 
                      ? `https://${selectedTenant.custom_domain}` 
                      : `${window.location.origin}/login?tenant=${selectedTenant.slug || selectedTenant.organization_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-1.5 bg-cerebro-accent hover:bg-cerebro-accent/90 text-cerebro-primary font-semibold rounded-lg transition-colors ml-2"
                    title={selectedTenant.custom_domain ? `Open ${selectedTenant.custom_domain}` : `Preview tenant: ${selectedTenant.slug || selectedTenant.name}`}
                  >
                    <Rocket className="h-4 w-4" />
                    {selectedTenant.custom_domain ? 'Launch' : 'Preview'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-zinc-900 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tenant-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-navigate-gold text-black'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        {activeTab === 'tenants' && (
          <TenantsListTab 
            organizations={organizations} 
            selectedTenant={selectedTenant}
            onSelectTenant={(org) => {
              setSelectedTenant(org);
              setActiveTab('users'); // Navigate to users after selecting
            }}
            onRefresh={fetchOrganizations}
            token={token}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
        
        {activeTab === 'users' && selectedTenant && (
          <UsersTab 
            tenant={selectedTenant}
            users={tenantUsers}
            token={token}
            onRefresh={fetchTenantUsers}
          />
        )}
        
        {activeTab === 'branding' && selectedTenant && (
          <BrandingSettings 
            token={token}
            organizationId={selectedTenant.org_id}
          />
        )}
        
        {activeTab === 'features' && selectedTenant && (
          <TenantFeatureManagement 
            token={token}
            organizationId={selectedTenant.org_id}
            organizationName={selectedTenant.name}
          />
        )}
        
        {activeTab === 'content' && (
          <ContentPublishingTab 
            courses={courses}
            publishedContent={publishedContent}
            organizations={organizations}
            selectedTenant={selectedTenant}
            token={token}
            onRefresh={() => {
              fetchPublishedContent();
              fetchCourses();
            }}
          />
        )}
        
        {activeTab === 'analytics' && (
          <AnalyticsTab 
            analytics={analytics}
            organizations={organizations}
            selectedTenant={selectedTenant}
            token={token}
          />
        )}
        
        {activeTab === 'setup-guide' && (
          <SetupGuideTab tenant={selectedTenant} />
        )}
        
        {/* No tenant selected warning */}
        {activeTab !== 'tenants' && activeTab !== 'content' && activeTab !== 'analytics' && activeTab !== 'setup-guide' && !selectedTenant && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Tenant Selected</h3>
            <p className="text-zinc-400 mb-4">Select a tenant from the Tenants tab to configure</p>
            <Button onClick={() => setActiveTab('tenants')} variant="outline" className="border-zinc-700">
              Go to Tenants
            </Button>
          </div>
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <CreateTenantModal 
          onClose={() => setShowCreateTenant(false)}
          onSuccess={(newOrg) => {
            fetchOrganizations();
            setSelectedTenant(newOrg);
            setShowCreateTenant(false);
            toast.success('Tenant created successfully!');
          }}
          token={token}
        />
      )}
    </div>
  );
}

// ==================== TENANTS LIST TAB ====================
function TenantsListTab({ organizations, selectedTenant, onSelectTenant, onRefresh, token, searchTerm, setSearchTerm }) {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  // Mirrors the create form so a tenant can be corrected after provisioning
  // instead of only at the moment it is made.
  const emptyEditForm = {
    name: '',
    slug: '',
    custom_domain: '',
    org_type: 'enterprise',
    contact_email: '',
    contact_name: '',
    city: '',
    country: '',
    max_users: 100
  };
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);

  // Load the selected tenant into the edit form whenever the dialog opens
  useEffect(() => {
    if (editTenant) {
      setEditForm({
        name: editTenant.name || '',
        slug: editTenant.slug || '',
        custom_domain: editTenant.custom_domain || '',
        org_type: editTenant.org_type || 'enterprise',
        contact_email: editTenant.contact_email || '',
        contact_name: editTenant.contact_name || '',
        city: editTenant.city || '',
        country: editTenant.country || '',
        max_users: editTenant.max_users ?? 100
      });
    }
  }, [editTenant]);

  const handleSaveTenant = async () => {
    if (!editForm.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    // An existing tenant always has a slug - it is how requests are routed to
    // it - so unlike the create form this one cannot fall back to generating.
    if (!editForm.slug.trim()) {
      toast.error('Slug is required');
      return;
    }

    if (!editForm.contact_email.trim()) {
      toast.error('Contact email is required');
      return;
    }

    const maxUsers = parseInt(editForm.max_users, 10);
    if (Number.isNaN(maxUsers)) {
      toast.error('Max users must be a number');
      return;
    }

    try {
      setSavingEdit(true);
      await axios.put(
        `${API}/master/organizations/${editTenant.org_id}`,
        {
          name: editForm.name.trim(),
          slug: editForm.slug.trim().toLowerCase(),
          // null clears the domain; an empty string would be stored and would
          // then match no host at all.
          custom_domain: editForm.custom_domain.trim().toLowerCase() || null,
          org_type: editForm.org_type,
          contact_email: editForm.contact_email.trim().toLowerCase(),
          contact_name: editForm.contact_name.trim() || null,
          city: editForm.city.trim() || null,
          country: editForm.country.trim() || null,
          max_users: maxUsers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Tenant updated');
      setEditTenant(null);
      onRefresh();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSavingEdit(false);
    }
  };
  const [deleting, setDeleting] = useState(false);
  
  const filteredOrgs = organizations.filter(org => 
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteTenant = async (org, permanent = false) => {
    // Prevent deleting master org
    if (org.org_id === 'org_navigate_master') {
      toast.error('Cannot delete the master organization');
      return;
    }
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/master/organizations/${org.org_id}?permanent=${permanent}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(permanent ? `${org.name} permanently deleted` : `${org.name} deactivated`);
      setDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">All Tenants ({organizations.length})</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tenants..."
              className="pl-10 w-64 bg-zinc-800 border-zinc-700"
            />
          </div>
          <Button variant="outline" onClick={onRefresh} className="border-zinc-700">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tenants Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOrgs.map(org => (
          <div 
            key={org.org_id}
            className={`p-4 rounded-xl border transition-all hover:shadow-lg ${
              selectedTenant?.org_id === org.org_id 
                ? 'bg-navigate-gold/10 border-navigate-gold/50' 
                : 'bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800'
            }`}
          >
            <div className="flex items-start gap-4">
              <div 
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => onSelectTenant(org)}
              >
                <div className="flex items-start gap-4">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-zinc-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{org.name}</h3>
                    <p className="text-sm text-zinc-500 truncate">
                      {org.custom_domain || `${org.slug}.cerebro.co.za`}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        org.org_type === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                        org.org_type === 'smb' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-zinc-700 text-zinc-300'
                      }`}>
                        {org.org_type}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {org.current_users || 0}/{org.max_users} users
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Status and Actions */}
              <div className="flex flex-col items-end gap-2">
                <div className={`w-2 h-2 rounded-full ${org.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditTenant(org);
                    }}
                    className="p-1.5 text-zinc-500 hover:text-navigate-gold hover:bg-navigate-gold/10 rounded transition-colors"
                    title="Edit tenant"
                    data-testid={`edit-tenant-${org.org_id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  {/* Delete button - don't show for master org */}
                  {org.org_id !== 'org_navigate_master' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(org);
                      }}
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Delete tenant"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOrgs.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No tenants found</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="h-6 w-6" />
                <h2 className="text-xl font-bold">Delete Tenant</h2>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-zinc-300 mb-4">
                Are you sure you want to delete <strong className="text-white">{deleteConfirm.name}</strong>?
              </p>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-zinc-400 mb-2">This tenant has:</p>
                <ul className="text-sm text-zinc-300 space-y-1">
                  <li>• {deleteConfirm.current_users || 0} users</li>
                  <li>• Domain: {deleteConfirm.slug}.cerebro.co.za</li>
                </ul>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleDeleteTenant(deleteConfirm, false)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Deactivate (Recoverable)
                </button>
                
                <button
                  onClick={() => handleDeleteTenant(deleteConfirm, true)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Permanently Delete
                </button>
                
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant */}
      {editTenant && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-navigate-surface border border-zinc-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-lg font-semibold text-white">Edit Tenant</h3>
              <p className="text-sm text-zinc-400 mt-1">{editTenant.org_id}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label className="text-zinc-300">Organization Name *</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="ACME Financial Services"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Slug (Subdomain) *</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={editForm.slug}
                    onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="acme-financial"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="edit-slug"
                  />
                  <span className="text-zinc-500 text-sm whitespace-nowrap">.cerebro.co.za</span>
                </div>
                <p className="text-xs text-amber-500/80 mt-2">
                  Changing the slug changes how this tenant is identified: its
                  subdomain, its <code className="bg-zinc-800 px-1 rounded">/api/sso/login/{editForm.slug || 'slug'}</code>{' '}
                  URL and any saved login links. Existing SSO redirect URIs must be
                  re-registered before staff can sign in again.
                </p>
              </div>

              <div>
                <Label className="text-zinc-300">Custom Domain</Label>
                <Input
                  value={editForm.custom_domain}
                  onChange={(e) => setEditForm({ ...editForm, custom_domain: e.target.value.toLowerCase() })}
                  placeholder="learning.acme.com"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  data-testid="edit-custom-domain"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  The hostname this organisation is served on. The bare domain and the
                  www form both resolve, whichever is stored. Leave empty to use
                  {' '}<code className="bg-zinc-800 px-1 rounded">{editForm.slug || editTenant.slug}.cerebro.co.za</code>.
                </p>
                <p className="text-xs text-amber-500/80 mt-2">
                  Setting this does not create DNS records or bind the domain in Azure.
                  Both are still required before the address will load.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Type</Label>
                  <select
                    value={editForm.org_type}
                    onChange={(e) => setEditForm({ ...editForm, org_type: e.target.value })}
                    className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="enterprise">Enterprise</option>
                    <option value="smb">SMB</option>
                    <option value="trial">Trial</option>
                    {/* Only offered for a tenant that already is the master org -
                        the create form cannot make one, and leaving it out here
                        would silently downgrade it on the next save. */}
                    {editForm.org_type === 'master' && <option value="master">Master</option>}
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-300">Max Users</Label>
                  <Input
                    type="number"
                    value={editForm.max_users}
                    onChange={(e) => setEditForm({ ...editForm, max_users: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Contact Email *</Label>
                <Input
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  placeholder="admin@acme.com"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Contact Name</Label>
                <Input
                  value={editForm.contact_name}
                  onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">City</Label>
                  <Input
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    placeholder="Johannesburg"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Country</Label>
                  <Input
                    value={editForm.country}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    placeholder="South Africa"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex gap-3">
              <button
                onClick={handleSaveTenant}
                disabled={savingEdit}
                className="flex-1 px-4 py-2 bg-navigate-gold hover:bg-navigate-gold-light text-black rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingEdit && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
              <button
                onClick={() => setEditTenant(null)}
                disabled={savingEdit}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== USERS TAB ====================
function UsersTab({ tenant, users, token, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [manualUsers, setManualUsers] = useState([
    { email: '', full_name: '', job_title: '', department: '' }
  ]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', role: 'learner', job_title: '' });
  // The API generates the password and returns it once - if it isn't shown
  // here, nobody can give the new user their credentials.
  const [createdUser, setCreatedUser] = useState(null);

  // Tenant-setup-fix: per-row user actions (delete, reset password, edit).
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [resetModalUser, setResetModalUser] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { user, temporary_password }

  // Edit user state
  const [editUser, setEditUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ full_name: '', job_title: '', department: '', role: 'learner' });
  const [updatingUser, setUpdatingUser] = useState(false);

  // User details modal state (clicking a user row)
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStartEdit = (user) => {
    setEditUser(user);
    setEditFormData({
      full_name: user.full_name || '',
      job_title: user.job_title || '',
      department: user.department || '',
      role: user.role || 'learner',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editUser?.user_id) return;
    setUpdatingUser(true);
    try {
      await axios.put(
        `${API}/admin/users/${editUser.user_id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${editFormData.full_name || editUser.email}`);
      setEditUser(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!tenant?.org_id) {
      toast.error('Select a tenant first');
      return;
    }

    setAddingUser(true);
    setCreatedUser(null);
    try {
      const response = await axios.post(
        `${API}/organizations/${tenant.org_id}/users`,
        {
          email: newUser.email.trim(),
          full_name: newUser.full_name.trim(),
          role: newUser.role,
          job_title: newUser.job_title.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCreatedUser(response.data.user);
      setNewUser({ email: '', full_name: '', role: 'learner', job_title: '' });
      toast.success(`${response.data.user.full_name} added to ${tenant.name}`);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!user?.user_id || !tenant?.org_id) return;
    setDeletingUserId(user.user_id);
    try {
      await axios.delete(
        `${API}/admin/users/${user.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${user.full_name || user.email} deactivated`);
      setDeleteConfirmUser(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleResetPassword = async (user, options = {}) => {
    if (!user?.user_id) return;
    setResettingUserId(user.user_id);
    try {
      const payload = {};
      if (options.password && options.password.trim()) {
        payload.password = options.password.trim();
      }
      if (typeof options.must_change_password === 'boolean') {
        payload.must_change_password = options.must_change_password;
      }

      const response = await axios.post(
        `${API}/admin/users/${user.user_id}/reset-password`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tempPw = response.data?.temporary_password;
      if (!tempPw) {
        toast.error('Reset succeeded but no temporary password was returned.');
        return;
      }
      setResetModalUser(null);
      setResetResult({
        user,
        temporary_password: tempPw,
        must_change_password: response.data?.must_change_password
      });
      toast.success(`Password reset for ${user.full_name || user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingUserId(null);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axios.post(
        `${API}/organizations/${tenant.org_id}/users/upload-csv`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setResults(response.data);
      toast.success(`${response.data.summary.created} users created`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const addManualRow = () => {
    setManualUsers([...manualUsers, { email: '', full_name: '', job_title: '', department: '' }]);
  };

  const updateManualUser = (index, field, value) => {
    const updated = [...manualUsers];
    updated[index][field] = value;
    setManualUsers(updated);
  };

  const handleManualUpload = async () => {
    const validUsers = manualUsers.filter(u => u.email && u.full_name);
    if (validUsers.length === 0) {
      toast.error('Please add at least one user with email and name');
      return;
    }

    try {
      setUploading(true);
      const response = await axios.post(
        `${API}/organizations/${tenant.org_id}/users/bulk`,
        { users: validUsers, send_welcome_email: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setResults(response.data);
      toast.success(`${response.data.summary.created} users created`);
      setManualUsers([{ email: '', full_name: '', job_title: '', department: '' }]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create users');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,full_name,job_title,department\njohn@example.com,John Doe,Compliance Officer,Compliance\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_upload_template.csv';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <Users className="h-5 w-5 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.length}</p>
          <p className="text-xs text-zinc-400">Total Users</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <GraduationCap className="h-5 w-5 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.filter(u => u.is_active).length}</p>
          <p className="text-xs text-zinc-400">Active Learners</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <TrendingUp className="h-5 w-5 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.reduce((sum, u) => sum + (u.completed_count || 0), 0)}</p>
          <p className="text-xs text-zinc-400">Completions</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <Activity className="h-5 w-5 text-orange-400 mb-2" />
          <p className="text-2xl font-bold text-white">{(tenant.max_users || 100) - users.length}</p>
          <p className="text-xs text-zinc-400">Seats Available</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input 
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700"
          />
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowBulkUpload(!showBulkUpload)}
            className="border-zinc-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button
            onClick={() => { setShowAddUser(!showAddUser); setShowBulkUpload(false); }}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            data-testid="add-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Add Single User */}
      {showAddUser && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-medium flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-navigate-gold" />
              Add user to {tenant?.name}
            </h4>
            <button
              type="button"
              onClick={() => { setShowAddUser(false); setCreatedUser(null); }}
              className="text-zinc-400 hover:text-white"
              aria-label="Close"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleAddUser} className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300 text-sm">Full name</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                placeholder="Jordan Smith"
                required
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="add-user-name"
              />
            </div>

            <div>
              <Label className="text-zinc-300 text-sm">Email address</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="jordan@company.com"
                required
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="add-user-email"
              />
            </div>

            <div>
              <Label className="text-zinc-300 text-sm">Role</Label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                data-testid="add-user-role"
              >
                <option value="learner">Learner</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin — can manage content and users</option>
                <option value="org_admin">Organisation Admin</option>
                <option value="auditor">Auditor</option>
              </select>
            </div>

            <div>
              <Label className="text-zinc-300 text-sm">Job title (optional)</Label>
              <Input
                value={newUser.job_title}
                onChange={(e) => setNewUser({ ...newUser, job_title: e.target.value })}
                placeholder="Compliance Manager"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>

            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={addingUser}
                className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                data-testid="add-user-submit"
              >
                {addingUser ? 'Adding...' : 'Add user'}
              </Button>
            </div>
          </form>

          {/* The password is returned once and never retrievable again. */}
          {createdUser && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {createdUser.full_name} added
              </p>
              <p className="text-sm text-zinc-300 mt-2">
                Temporary password — copy it now, it won't be shown again:
              </p>
              <code className="block mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-navigate-gold text-sm break-all">
                {createdUser.temp_password}
              </code>
              <p className="text-xs text-zinc-500 mt-2">
                They'll be asked to set their own password at first sign-in.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Bulk Upload Section */}
      {showBulkUpload && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* CSV Upload */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-green-400" />
                Upload CSV
              </h4>
              <label className="block">
                <div className="border-2 border-dashed border-zinc-600 rounded-lg p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors">
                  <Upload className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">
                    {uploading ? 'Uploading...' : 'Click to upload CSV'}
                  </p>
                </div>
                <input 
                  type="file" 
                  accept=".csv"
                  onChange={handleCSVUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <Button 
                variant="outline" 
                onClick={downloadTemplate}
                className="mt-3 border-zinc-700 text-zinc-300 w-full"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* Manual Entry */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-400" />
                Manual Entry
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {manualUsers.map((user, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Email *"
                      value={user.email}
                      onChange={(e) => updateManualUser(idx, 'email', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-sm"
                    />
                    <Input
                      placeholder="Full Name *"
                      value={user.full_name}
                      onChange={(e) => updateManualUser(idx, 'full_name', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={addManualRow} size="sm" className="border-zinc-700">
                  <Plus className="h-3 w-3 mr-1" />
                  Add Row
                </Button>
                <Button 
                  onClick={handleManualUpload}
                  disabled={uploading}
                  size="sm"
                  className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                >
                  Create Users
                </Button>
              </div>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="border-t border-zinc-700 pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{results.summary.created}</p>
                  <p className="text-xs text-zinc-400">Created</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">{results.summary.skipped}</p>
                  <p className="text-xs text-zinc-400">Skipped</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{results.summary.failed}</p>
                  <p className="text-xs text-zinc-400">Failed</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-zinc-800/30 border border-zinc-700 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">User</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Department</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Enrollments</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Completed</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Status</th>
              <th className="text-right p-4 text-xs font-medium text-zinc-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr
                key={user.user_id}
                onClick={() => setSelectedUserDetails(user)}
                className="border-t border-zinc-700 hover:bg-zinc-800/60 cursor-pointer transition-colors group"
                title="Click to view user details"
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-white font-medium text-xs">
                        {user.full_name?.split(' ').map(n => n[0]).join('') || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium group-hover:text-navigate-gold transition-colors">
                        {user.full_name}
                      </p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-sm text-zinc-400">{user.department || '—'}</td>
                <td className="p-4 text-sm text-zinc-300">{user.enrollments_count || 0}</td>
                <td className="p-4 text-sm text-green-400">{user.completed_count || 0}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded ${
                    user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleStartEdit(user)}
                      data-testid={`edit-user-${user.user_id}`}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
                      title="Edit user details"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetModalUser(user)}
                      disabled={resettingUserId === user.user_id || user.is_active === false}
                      data-testid={`reset-password-${user.user_id}`}
                      className="p-1.5 text-zinc-500 hover:text-navigate-gold hover:bg-navigate-gold/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={user.is_active === false ? 'Inactive users cannot sign in to reset' : 'Reset password'}
                    >
                      {resettingUserId === user.user_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmUser(user)}
                      disabled={deletingUserId === user.user_id || user.is_active === false}
                      data-testid={`deactivate-user-${user.user_id}`}
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={user.is_active === false ? 'Already inactive' : 'Deactivate user'}
                    >
                      {deletingUserId === user.user_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-zinc-500">
            No users found
          </div>
        )}
      </div>

      {/* Tenant-setup-fix: Deactivate-user confirmation modal. */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="h-6 w-6" />
                <h2 className="text-xl font-bold">Deactivate user</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-zinc-300 mb-4">
                Deactivate <strong className="text-white">{deleteConfirmUser.full_name || deleteConfirmUser.email}</strong>?
                The user will keep their history (enrollments, certificates) but cannot sign in.
              </p>
              <div className="flex justify-end gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmUser(null)}
                  disabled={deletingUserId !== null}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteUser(deleteConfirmUser)}
                  disabled={deletingUserId !== null}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {deletingUserId === deleteConfirmUser.user_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal (clicking on row) */}
      {selectedUserDetails && (
        <UserDetailsModal
          user={selectedUserDetails}
          onClose={() => setSelectedUserDetails(null)}
          onEdit={() => {
            const u = selectedUserDetails;
            setSelectedUserDetails(null);
            handleStartEdit(u);
          }}
          onResetPassword={() => {
            const u = selectedUserDetails;
            setSelectedUserDetails(null);
            setResetModalUser(u);
          }}
        />
      )}

      {/* Edit User Modal */}
      {editUser && (
        <EditUserModal
          user={editUser}
          formData={editFormData}
          setFormData={setEditFormData}
          onSave={handleSaveEdit}
          onClose={() => setEditUser(null)}
          loading={updatingUser}
        />
      )}

      {/* Reset Password Prompt Modal (choose auto or custom) */}
      {resetModalUser && (
        <ResetPasswordPromptModal
          user={resetModalUser}
          onReset={(options) => handleResetPassword(resetModalUser, options)}
          onClose={() => setResetModalUser(null)}
          loading={resettingUserId === resetModalUser.user_id}
        />
      )}

      {/* Reset Password Result Modal (shows temporary password with Copy) */}
      {resetResult && (
        <ResetPasswordModal
          result={resetResult}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

// Modal for viewing full user details (opens on clicking row)
function UserDetailsModal({ user, onClose, onEdit, onResetPassword }) {
  const [copiedId, setCopiedId] = useState(false);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(user.user_id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      toast.error('Failed to copy ID');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const roleLabels = {
    learner: 'Learner',
    instructor: 'Instructor',
    admin: 'Tenant Admin',
    org_admin: 'Organization Admin',
    auditor: 'Auditor',
    super_admin: 'Platform Admin',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/80">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-gradient-to-br from-blue-600 via-purple-600 to-navigate-gold rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg shrink-0">
                {user.full_name?.split(' ').map(n => n[0]).join('') || '?'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  {user.full_name || 'Unnamed User'}
                </h2>
                <p className="text-sm text-zinc-400">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 font-medium">
                    {roleLabels[user.role] || user.role}
                  </span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                    user.is_active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-zinc-700/50 text-zinc-400 border border-zinc-700'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-300 p-1 rounded-lg text-lg leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content Details Grid */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Metadata Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-500">Job Title</p>
              <p className="text-sm font-medium text-zinc-200 mt-0.5">{user.job_title || '—'}</p>
            </div>
            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-500">Department</p>
              <p className="text-sm font-medium text-zinc-200 mt-0.5">{user.department || '—'}</p>
            </div>
            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-500">Course Enrollments</p>
              <p className="text-sm font-semibold text-zinc-200 mt-0.5">{user.enrollments_count || 0}</p>
            </div>
            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-500">Completed Courses</p>
              <p className="text-sm font-semibold text-green-400 mt-0.5">{user.completed_count || 0}</p>
            </div>
          </div>

          {/* User ID & Organization Details */}
          <div className="p-3.5 bg-zinc-950 rounded-lg border border-zinc-800 space-y-2.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">User ID</span>
              <div className="flex items-center gap-1.5 font-mono text-zinc-300">
                <span>{user.user_id}</span>
                <button
                  type="button"
                  onClick={copyId}
                  className="p-1 hover:text-navigate-gold text-zinc-500 transition-colors"
                  title="Copy User ID"
                >
                  {copiedId ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Organization ID</span>
              <span className="font-mono text-zinc-300">{user.organization_id || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Created / Joined</span>
              <span className="text-zinc-300">{formatDate(user.created_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Password Change Required</span>
              <span className={user.must_change_password ? 'text-amber-400 font-medium' : 'text-zinc-400'}>
                {user.must_change_password ? 'Yes (Next Login)' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit User
            </button>
            <button
              type="button"
              onClick={onResetPassword}
              disabled={user.is_active === false}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-navigate-gold rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Reset Password
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal for editing user profile (Name, Role, Department, Job Title)
function EditUserModal({ user, formData, setFormData, onSave, onClose, loading }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md shadow-2xl">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Pencil className="h-5 w-5 text-navigate-gold" />
            <h2 className="text-xl font-bold">Edit User</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>
        <form onSubmit={onSave} className="p-6 space-y-4">
          <div>
            <Label className="text-xs text-zinc-400">Email Address (Read-only)</Label>
            <Input
              type="text"
              value={user.email}
              disabled
              className="mt-1 bg-zinc-950/60 border-zinc-800 text-zinc-500 cursor-not-allowed"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Full Name</Label>
            <Input
              type="text"
              required
              value={formData.full_name}
              onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="e.g. John Doe"
              className="mt-1 bg-zinc-950 border-zinc-800 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Job Title</Label>
              <Input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                placeholder="e.g. Software Engineer"
                className="mt-1 bg-zinc-950 border-zinc-800 text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Department</Label>
              <Input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g. Compliance"
                className="mt-1 bg-zinc-950 border-zinc-800 text-white"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Role</Label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-navigate-gold"
            >
              <option value="learner">Learner</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
              <option value="org_admin">Org Admin</option>
              <option value="auditor">Auditor</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-navigate-gold hover:bg-navigate-gold/90 text-black font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal for confirming and setting password options (Auto-generate vs Custom)
function ResetPasswordPromptModal({ user, onReset, onClose, loading }) {
  const [mode, setMode] = useState('auto'); // 'auto' | 'custom'
  const [customPassword, setCustomPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'custom' && customPassword.trim().length < 8) {
      toast.error('Custom password must be at least 8 characters long');
      return;
    }
    onReset({
      password: mode === 'custom' ? customPassword.trim() : undefined,
      must_change_password: mustChangePassword,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md shadow-2xl">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 text-navigate-gold">
            <Key className="h-6 w-6" />
            <h2 className="text-xl font-bold">Reset Password</h2>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            For <strong className="text-white">{user.full_name || user.email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-300">Password Method</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('auto')}
                className={`py-2 px-3 rounded-lg text-xs font-medium border text-center transition-all ${
                  mode === 'auto'
                    ? 'border-navigate-gold bg-navigate-gold/10 text-navigate-gold'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white'
                }`}
              >
                Auto-generate
              </button>
              <button
                type="button"
                onClick={() => setMode('custom')}
                className={`py-2 px-3 rounded-lg text-xs font-medium border text-center transition-all ${
                  mode === 'custom'
                    ? 'border-navigate-gold bg-navigate-gold/10 text-navigate-gold'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white'
                }`}
              >
                Enter custom password
              </button>
            </div>
          </div>

          {mode === 'custom' ? (
            <div>
              <Label className="text-xs text-zinc-400">New Password (Min 8 characters)</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                  placeholder="Enter strong password"
                  className="bg-zinc-950 border-zinc-800 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800/80 text-xs text-zinc-400">
              A secure, high-entropy password will be generated automatically and displayed once for you to copy.
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="mustChangePw"
              checked={mustChangePassword}
              onChange={(e) => setMustChangePassword(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-950 text-navigate-gold focus:ring-0"
            />
            <label htmlFor="mustChangePw" className="text-xs text-zinc-300 cursor-pointer">
              Force password change on next sign-in
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-navigate-gold hover:bg-navigate-gold/90 text-black font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Tenant-setup-fix: extracted child so UsersTab's JSX stays readable.
// Shows the temporary password once, with a Copy-to-clipboard affordance and
// a clear "shown only once" warning.
function ResetPasswordModal({ result, onClose }) {
  const { user, temporary_password } = result;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy - please select and copy manually.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 text-navigate-gold">
            <RefreshCw className="h-6 w-6" />
            <h2 className="text-xl font-bold">Temporary password set</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-zinc-300">
            New temporary password for <strong className="text-white">{user.full_name || user.email}</strong>:
          </p>
          <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 flex items-center gap-2">
            <code
              data-testid="temp-password-value"
              className="flex-1 font-mono text-sm text-navigate-gold break-all select-all"
            >
              {temporary_password}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              data-testid="copy-temp-password"
              className="shrink-0 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md text-sm flex items-center gap-1"
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-amber-400 bg-amber-500/10 rounded-md p-3">
            <AlertTriangle className="inline h-3 w-3 mr-1" />
            This password is shown <strong>only once</strong>. The user will be
            asked to set a new password on first sign-in.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-navigate-gold hover:bg-navigate-gold/90 text-black rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== CONTENT PUBLISHING TAB ====================
function ContentPublishingTab({ courses, publishedContent, organizations, selectedTenant, token, onRefresh }) {
  const [publishing, setPublishing] = useState(false);
  const [contentType, setContentType] = useState('courses'); // 'courses' | 'external_courses'
  const [externalCourses, setExternalCourses] = useState([]);
  const [loadingExternal, setLoadingExternal] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [publishScope, setPublishScope] = useState('all');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [mandatory, setMandatory] = useState(false);
  const [unpublishingId, setUnpublishingId] = useState(null);

  useEffect(() => {
    fetchExternalCourses();
  }, [token]);

  const fetchExternalCourses = async () => {
    setLoadingExternal(true);
    try {
      const res = await axios.get(`${API}/external-learning/modules?status=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExternalCourses(res.data?.modules || res.data || []);
    } catch (err) {
      console.warn('Failed to fetch external modules:', err);
    } finally {
      setLoadingExternal(false);
    }
  };

  // Symptom #4 fix: the search/selectable pool now includes every course for the
  // selected tenant (or every course globally if no tenant is selected), not only
  // already-published ones. Each row is tagged so the admin sees what is already
  // published and would no-op on re-publish.
  const publishedCourses = courses.filter(c => c.is_published);
  const tenantCourses = selectedTenant
    ? courses.filter(c => c.org_id === selectedTenant.org_id)
    : courses;
  const activeExternalCourses = externalCourses.filter(m => m.status === 'active' || !m.status);
  const publishedInternalIds = new Set(publishedCourses.map(c => c.course_id));

  const currentItems = contentType === 'courses' ? tenantCourses : activeExternalCourses;
  const filteredItems = currentItems.filter(item => {
    const title = (item.title || item.name || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return title.includes(query);
  });

  const getItemId = (item) => item.course_id || item.module_id;
  const getItemTitle = (item) => item.title || item.name || 'Untitled';
  const isAlreadyPublished = (item) =>
    contentType === 'courses' && publishedInternalIds.has(getItemId(item));

  // Symptom #3 fix: unpublish handler. The backend endpoint
  //   POST /api/master/content/{type}/{id}/unpublish
  // already exists (routes/master_admin.py). The published lists at the bottom
  // of the page previously had no per-row action. We add one here.
  // Type/id derivation lives in utils/tenantUnpublish.pickUnpublishTarget so it
  // is testable in isolation.
  const handleUnpublish = async (item, explicitContentType) => {
    const target = pickUnpublishTarget(item, explicitContentType || contentType);
    if (!target.id) return;
    if (!window.confirm(
      `Unpublish "${target.label}"? It will be removed from all tenants that currently receive it.`
    )) return;
    setUnpublishingId(target.id);
    try {
      await axios.post(
        `${API}/master/content/${target.type}/${target.id}/unpublish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Unpublished ${target.label}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    } finally {
      setUnpublishingId(null);
    }
  };

  // "Publishable" items = filtered items minus the ones that are already published,
  // since re-publishing an already-published item would either no-op or fail depending
  // on the backend's idempotency behaviour. We still SHOW already-published rows
  // (with a "Published" badge), they just aren't selectable.
  const publishableItems = filteredItems.filter(item => !isAlreadyPublished(item));

  const isAllFilteredSelected = publishableItems.length > 0 &&
    publishableItems.every(item => selectedCourseIds.includes(getItemId(item)));

  const toggleSelect = (id) => {
    setSelectedCourseIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allPublishableIds = publishableItems.map(getItemId);
      setSelectedCourseIds(Array.from(new Set([...selectedCourseIds, ...allPublishableIds])));
    } else {
      const filteredIdSet = new Set(publishableItems.map(getItemId));
      setSelectedCourseIds(prev => prev.filter(id => !filteredIdSet.has(id)));
    }
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
    setSelectedCourseIds([]);
    setSearchQuery('');
  };

  const handlePublish = async () => {
    if (selectedCourseIds.length === 0) {
      toast.error('Please select at least one course to publish');
      return;
    }

    setPublishing(true);
    try {
      await axios.post(
        `${API}/master/content/batch-publish`,
        {
          content_type: contentType === 'external_courses' ? 'external_course' : 'course',
          content_ids: selectedCourseIds,
          publish_scope: publishScope,
          tenant_ids: publishScope === 'selected' ? selectedTenants : null,
          mandatory: mandatory,
          auto_enroll_new_users: false,
          sync_updates: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Published ${selectedCourseIds.length} ${contentType === 'external_courses' ? 'external courses' : 'courses'} to tenants!`);
      setSelectedCourseIds([]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish content');
    } finally {
      setPublishing(false);
    }
  };

  const internalPublished = publishedContent?.course || [];
  const externalPublished = publishedContent?.external_course || publishedContent?.external_module || [];

  return (
    <div className="space-y-6">
      {/* Publish Form */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Send className="h-5 w-5 text-navigate-gold" />
          Publish Content to Tenants
        </h3>

        {/* Content Type Switcher */}
        <div className="flex gap-2 mb-6 p-1 bg-zinc-900 rounded-lg border border-zinc-700 max-w-md">
          <button
            type="button"
            onClick={() => handleContentTypeChange('courses')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              contentType === 'courses'
                ? 'bg-navigate-gold text-black shadow-sm font-semibold'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Internal Courses ({tenantCourses.length})
          </button>
          <button
            type="button"
            onClick={() => handleContentTypeChange('external_courses')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              contentType === 'external_courses'
                ? 'bg-navigate-gold text-black shadow-sm font-semibold'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Globe className="h-4 w-4" />
            External Courses ({activeExternalCourses.length})
          </button>
        </div>

        <div className="space-y-5">
          {/* Multi-Course Selection Box */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-zinc-300">
                Select {contentType === 'external_courses' ? 'External Courses' : 'Courses'} ({selectedCourseIds.length} selected)
              </Label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-navigate-gold cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAllFilteredSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded text-navigate-gold focus:ring-navigate-gold"
                  />
                  Select All Filtered
                </label>
                {selectedCourseIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCourseIds([])}
                    className="text-xs text-zinc-400 hover:text-red-400 transition-colors"
                  >
                    Clear Selection
                  </button>
                )}
              </div>
            </div>

            {/* Search filter */}
            <div className="relative mb-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <Input
                type="text"
                placeholder={`Search ${contentType === 'external_courses' ? 'external courses' : 'courses'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500"
              />
            </div>

            {/* Selectable Course List */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-2 max-h-56 overflow-y-auto space-y-1">
              {loadingExternal && contentType === 'external_courses' ? (
                <div className="flex items-center justify-center py-6 text-zinc-400 gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading external courses...
                </div>
              ) : publishableItems.length === 0 && filteredItems.length === 0 ? (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  No {contentType === 'external_courses' ? 'external courses' : 'courses'} found
                </div>
              ) : publishableItems.length === 0 ? (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  All {contentType === 'external_courses' ? 'external courses' : 'courses'} in this view are already published.
                </div>
              ) : (
                filteredItems.map(item => {
                  const id = getItemId(item);
                  const isChecked = selectedCourseIds.includes(id);
                  const already = isAlreadyPublished(item);
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-3 p-2.5 rounded-md transition-colors ${
                        already
                          ? 'cursor-not-allowed opacity-60 text-zinc-500'
                          : isChecked
                          ? 'cursor-pointer bg-navigate-gold/10 border border-navigate-gold/30 text-white'
                          : 'cursor-pointer hover:bg-zinc-800 text-zinc-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(id)}
                        disabled={already}
                        className="rounded text-navigate-gold focus:ring-navigate-gold disabled:opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{getItemTitle(item)}</p>
                        <p className="text-xs text-zinc-500 truncate">
                          {item.provider ? `${item.provider} • ` : ''}
                          {item.category || 'General'}
                          {item.cpd_points ? ` • ${item.cpd_points} CPD` : ''}
                        </p>
                      </div>
                      {already && (
                        <span className="shrink-0 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded font-medium">
                          Published
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Publish Settings Grid */}
          <div className="grid md:grid-cols-2 gap-6 pt-2">
            <div>
              <Label className="text-zinc-300 mb-2 block">Publish To</Label>
              <select
                value={publishScope}
                onChange={(e) => setPublishScope(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white"
              >
                <option value="all">All Tenants</option>
                <option value="selected">Selected Tenants</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none mt-6">
                <input
                  type="checkbox"
                  checked={mandatory}
                  onChange={(e) => setMandatory(e.target.checked)}
                  className="rounded text-navigate-gold focus:ring-navigate-gold"
                />
                Mandatory (tenants cannot disable this content)
              </label>
            </div>

            {publishScope === 'selected' && (
              <div className="md:col-span-2">
                <Label className="text-zinc-300 mb-2 block">Select Tenants</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-3 bg-zinc-900 rounded-lg border border-zinc-700">
                  {organizations.map(org => (
                    <label key={org.org_id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(org.org_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTenants([...selectedTenants, org.org_id]);
                          } else {
                            setSelectedTenants(selectedTenants.filter(id => id !== org.org_id));
                          }
                        }}
                        className="rounded"
                      />
                      {org.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="md:col-span-2 pt-2">
              <Button
                onClick={handlePublish}
                disabled={selectedCourseIds.length === 0 || publishing}
                className="bg-navigate-gold hover:bg-navigate-gold/90 text-black font-semibold px-6"
              >
                {publishing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Publishing {selectedCourseIds.length} items...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Publish {selectedCourseIds.length > 0 ? `${selectedCourseIds.length} ` : ''}{contentType === 'external_courses' ? 'External ' : ''}Course{selectedCourseIds.length === 1 ? '' : 's'} to {publishScope === 'all' ? 'All Tenants' : `${selectedTenants.length} Tenants`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Published Content Lists */}
      <div className="space-y-6">
        {/* Internal Courses */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-green-400" />
            Published Internal Courses ({internalPublished.length})
          </h3>
          <div className="space-y-2">
            {internalPublished.map(course => (
              <div key={course.course_id} className="flex items-center justify-between p-3.5 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                <div>
                  <p className="text-white font-medium">{course.title}</p>
                  <p className="text-xs text-zinc-500">
                    {course.publish_scope === 'all' ? 'All tenants' : 'Selected tenants'}
                    {' '}• {course.active_tenant_count || 0} active
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded font-medium">
                    Published
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUnpublish({ course_id: course.course_id, title: course.title, module_id: course.course_id }, 'course')}
                    disabled={unpublishingId === course.course_id}
                    data-testid={`unpublish-course-${course.course_id}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                    title="Unpublish from all tenants"
                  >
                    {unpublishingId === course.course_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
            {internalPublished.length === 0 && (
              <p className="text-zinc-500 text-center py-6 bg-zinc-900/30 rounded-lg border border-zinc-800 text-sm">
                No internal courses published to tenants yet
              </p>
            )}
          </div>
        </div>

        {/* External Courses */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            Published External Courses ({externalPublished.length})
          </h3>
          <div className="space-y-2">
            {externalPublished.map(course => (
              <div key={course.module_id || course.course_id} className="flex items-center justify-between p-3.5 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                <div>
                  <p className="text-white font-medium">{course.title}</p>
                  <p className="text-xs text-zinc-500">
                    {course.publish_scope === 'all' ? 'All tenants' : 'Selected tenants'}
                    {' '}• {course.active_tenant_count || 0} active
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-medium">
                    Published
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUnpublish({
                      module_id: course.module_id || course.course_id,
                      course_id: course.module_id || course.course_id,
                      title: course.title
                    }, 'external_courses')}
                    disabled={unpublishingId === (course.module_id || course.course_id)}
                    data-testid={`unpublish-external-${course.module_id || course.course_id}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                    title="Unpublish from all tenants"
                  >
                    {unpublishingId === (course.module_id || course.course_id) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
            {externalPublished.length === 0 && (
              <p className="text-zinc-500 text-center py-6 bg-zinc-900/30 rounded-lg border border-zinc-800 text-sm">
                No external courses published to tenants yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== ANALYTICS TAB ====================
function AnalyticsTab({ analytics, organizations, selectedTenant, token }) {
  const overview = analytics?.overview || {};
  
  return (
    <div className="space-y-6">
      {/* Platform Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Platform Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Building2 className="h-5 w-5 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_tenants || 0}</p>
            <p className="text-sm text-zinc-400">Total Tenants</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Users className="h-5 w-5 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_users || 0}</p>
            <p className="text-sm text-zinc-400">Total Users</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <BookOpen className="h-5 w-5 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_enrollments || 0}</p>
            <p className="text-sm text-zinc-400">Enrollments</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Activity className="h-5 w-5 text-orange-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.completion_rate || 0}%</p>
            <p className="text-sm text-zinc-400">Completion Rate</p>
          </div>
        </div>
      </div>

      {/* Top Tenants */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-navigate-gold" />
            Top Tenants by Users
          </h3>
          <div className="space-y-3">
            {(analytics?.top_tenants || []).slice(0, 5).map((tenant, idx) => (
              <div key={tenant.org_id} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-navigate-gold text-black' : 'bg-zinc-700 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-white">{tenant.name}</span>
                </div>
                <span className="text-zinc-400">{tenant.user_count} users</span>
              </div>
            ))}
            {(!analytics?.top_tenants || analytics.top_tenants.length === 0) && (
              <p className="text-zinc-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-navigate-gold" />
            Tenants by Type
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics?.tenants_by_type || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                <span className="text-white capitalize">{type.replace('_', ' ')}</span>
                <span className="px-3 py-1 bg-zinc-700 rounded-full text-sm text-white">{count}</span>
              </div>
            ))}
            {Object.keys(analytics?.tenants_by_type || {}).length === 0 && (
              <p className="text-zinc-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== CREATE TENANT MODAL ====================
function CreateTenantModal({ onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    custom_domain: '',
    org_type: 'enterprise',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    city: '',
    country: 'South Africa',
    max_users: 100
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Either slug or custom_domain must be provided
    if (!formData.slug.trim() && !formData.custom_domain.trim()) {
      toast.error('Please provide either a subdomain or a custom domain');
      return;
    }
    
    setLoading(true);
    
    try {
      // Only send slug if it has a value
      const payload = {
        ...formData,
        slug: formData.slug.trim() || null,
        custom_domain: formData.custom_domain.trim() || null
      };
      
      const response = await axios.post(
        `${API}/master/organizations`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="h-5 w-5 text-navigate-gold" />
            Create New Tenant
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-zinc-300">Organization Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ACME Financial Services"
              className="mt-1 bg-zinc-800 border-zinc-700"
              required
            />
          </div>
          
          <div>
            <Label className="text-zinc-300">
              Slug (Subdomain) {!formData.custom_domain.trim() && !formData.slug.trim() && <span className="text-amber-500">*</span>}
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="acme-financial"
                className="bg-zinc-800 border-zinc-700"
              />
              <span className="text-zinc-500 text-sm whitespace-nowrap">.cerebro.co.za</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {formData.custom_domain.trim() 
                ? "Optional when using custom domain" 
                : "Required if no custom domain is provided"}
            </p>
          </div>
          
          <div>
            <Label className="text-zinc-300">
              Custom Domain {!formData.slug.trim() && !formData.custom_domain.trim() && <span className="text-amber-500">*</span>}
            </Label>
            <Input
              value={formData.custom_domain}
              onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase() })}
              placeholder="learning.acme.com"
              className="mt-1 bg-zinc-800 border-zinc-700"
            />
            <p className="text-xs text-zinc-500 mt-1">
              {formData.slug.trim()
                ? "Optional when using subdomain"
                : formData.custom_domain.trim()
                  ? "Client must configure DNS CNAME to point to this app"
                  : "Required if no subdomain is provided"}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">Type</Label>
              <select
                value={formData.org_type}
                onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
              >
                <option value="enterprise">Enterprise</option>
                <option value="smb">SMB</option>
                <option value="trial">Trial</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-300">Max Users</Label>
              <Input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-zinc-300">Contact Email *</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              placeholder="admin@acme.com"
              className="mt-1 bg-zinc-800 border-zinc-700"
              required
            />
          </div>
          
          <div>
            <Label className="text-zinc-300">Contact Name</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="John Smith"
              className="mt-1 bg-zinc-800 border-zinc-700"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Johannesburg"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-zinc-700">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-navigate-gold hover:bg-navigate-gold/90 text-black">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== SETUP GUIDE TAB ====================

// The host tenants fall back to when they have no custom domain of their own.
const TENANT_FALLBACK_DOMAIN = 'cerebro.co.za';

/**
 * The values for the tenant currently selected, rather than examples.
 *
 * Everything here is computed from that tenant and from the host serving this
 * page. The two things a browser cannot know - the App Service hostname behind
 * a front door, and the Azure verification ID - are asked for once and kept
 * locally, so the records below are complete instead of half-placeholder.
 */
function TenantSetupValues({
  tenant,
  appServiceHost,
  setAppServiceHost,
  verificationId,
  setVerificationId,
  renderCopyButton
}) {
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const hosts = tenantSignInHosts(tenant, TENANT_FALLBACK_DOMAIN);
  const uris = redirectUris(hosts);
  const records = dnsRecords(tenant?.custom_domain, { appServiceHost }, verificationId);
  const loginPath = ssoLoginPath(tenant);
  const ssoCall = ssoConfigExample(tenant);

  // Resolving the stored domain needs no DNS and no Azure binding, so it
  // separates "the tenant record is wrong" from "the domain is not bound yet"
  // before anyone starts changing records.
  const checkHost = async () => {
    const host = tenant?.custom_domain || hosts[0];
    if (!host) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await axios.get(`${API}/tenant/by-host`, { params: { host } });
      setCheckResult({ ok: res.data?.slug === tenant?.slug, slug: res.data?.slug, host });
    } catch (err) {
      setCheckResult({ ok: false, slug: null, host });
    } finally {
      setChecking(false);
    }
  };

  if (!tenant) {
    return (
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <p className="text-zinc-400">
          Select a tenant on the <strong className="text-white">Tenants</strong> tab to see its own
          hostnames, redirect URIs and DNS records here. The steps below stay generic until you do.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 rounded-xl p-6 border border-navigate-gold/30 space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Building2 className="h-5 w-5 text-navigate-gold" />
          {tenant.name}
        </h3>
        <p className="text-sm text-zinc-400 mt-1">
          Exact values for this tenant. Everything below is copyable as-is.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="bg-zinc-900 rounded-lg p-3">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Slug</p>
          <code className="text-white break-all">{tenant.slug}</code>
        </div>
        <div className="bg-zinc-900 rounded-lg p-3">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Organisation ID</p>
          <code className="text-white break-all">{tenant.org_id}</code>
        </div>
      </div>

      <div>
        <p className="font-medium text-white mb-2">Hostnames this tenant answers on</p>
        <div className="space-y-2">
          {hosts.map((h) => (
            <div key={h} className="flex items-center justify-between bg-zinc-900 px-3 py-2 rounded text-sm">
              <code className="text-white break-all">https://{h}</code>
              {renderCopyButton(`https://${h}`, `host-${h}`)}
            </div>
          ))}
        </div>
        {tenant.custom_domain ? (
          <p className="text-xs text-zinc-500 mt-2">
            The apex and www forms both resolve to this tenant once bound, so register both.
          </p>
        ) : (
          <p className="text-xs text-amber-500/80 mt-2">
            No custom domain stored, so this tenant falls back to its slug subdomain.
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-medium text-white">Does the stored domain resolve?</p>
          <button
            onClick={checkHost}
            disabled={checking}
            className="px-3 py-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {checking && <Loader2 className="h-3 w-3 animate-spin" />}
            Check now
          </button>
        </div>
        {checkResult && (
          <div className={`text-sm px-3 py-2 rounded ${checkResult.ok ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'}`}>
            {checkResult.ok
              ? `${checkResult.host} resolves to this tenant. DNS and the Azure binding are the only things left.`
              : checkResult.slug
                ? `${checkResult.host} resolves to "${checkResult.slug}", not this tenant. Fix the stored domain first.`
                : `${checkResult.host} resolves to no tenant. The stored domain is wrong - no DNS work will fix that.`}
          </div>
        )}
        <p className="text-xs text-zinc-500 mt-2">
          This asks the API directly and needs no DNS record and no Azure binding, so it tells you
          whether the tenant record is right before you change anything.
        </p>
      </div>

      <div>
        <p className="font-medium text-white mb-2">Entra redirect URIs</p>
        {uris.length > 0 ? (
          <div className="space-y-2">
            {uris.map((uri, i) => (
              <div key={uri} className="flex items-center justify-between bg-zinc-900 px-3 py-2 rounded text-sm">
                <code className="text-white break-all">{uri}</code>
                {renderCopyButton(uri, `panel-uri-${i}`)}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-500">No hostname to build a redirect URI from.</p>
        )}
        <p className="text-xs text-zinc-500 mt-2">
          Register every one on the app registration. A hostname that is missing here fails at
          sign-in, not at configuration time.
        </p>
        {loginPath && (
          <div className="flex items-center justify-between bg-zinc-900 px-3 py-2 rounded text-sm mt-2">
            <span className="text-zinc-400">Sign-in entry point</span>
            <code className="text-white break-all">{loginPath}</code>
            {renderCopyButton(loginPath, 'sso-login-path')}
          </div>
        )}
      </div>

      {tenant.custom_domain && (
        <div>
          <p className="font-medium text-white mb-2">DNS records for {tenant.custom_domain}</p>
          <div className="space-y-2">
            {records.map((record) => (
              <div key={`${record.type}-${record.name}`} className="flex items-center justify-between bg-zinc-900 px-3 py-2 rounded text-sm font-mono gap-3">
                <span className="text-zinc-400 w-14 flex-shrink-0">{record.type}</span>
                <span className="text-white flex-shrink-0">{record.name}</span>
                {record.value ? (
                  <>
                    <span className="text-green-400 break-all flex-1 text-right">{record.value}</span>
                    {renderCopyButton(record.value, `panel-dns-${record.type}-${record.name}`)}
                  </>
                ) : (
                  <span className="text-amber-500/80 flex-1 text-right text-xs">{record.hint}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-zinc-700 pt-4 space-y-3">
        <p className="text-sm text-zinc-400">
          These two come from Azure and cannot be read from the browser. Enter them once and they
          are remembered on this device.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-300 text-xs">App Service hostname</Label>
            <Input
              value={appServiceHost}
              onChange={(e) => {
                const v = e.target.value.trim().toLowerCase();
                setAppServiceHost(v);
                storeSetupValue(APP_SERVICE_HOST_KEY, v);
              }}
              placeholder="cerebro-lms.azurewebsites.net"
              className="mt-1 bg-zinc-800 border-zinc-700 text-sm"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Custom Domain Verification ID</Label>
            <Input
              value={verificationId}
              onChange={(e) => {
                const v = e.target.value.trim();
                setVerificationId(v);
                storeSetupValue(VERIFICATION_ID_KEY, v);
              }}
              placeholder="App Service -> Custom domains"
              className="mt-1 bg-zinc-800 border-zinc-700 text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <p className="font-medium text-white mb-2">Enable SSO for this tenant</p>
        <pre className="bg-zinc-900 rounded-lg p-3 text-xs text-zinc-300 overflow-x-auto">
{`${ssoCall.method} ${ssoCall.path}
${JSON.stringify(ssoCall.body, null, 2)}`}
        </pre>
        <p className="text-xs text-zinc-500 mt-2">
          <code className="bg-zinc-800 px-1 rounded">sso_provider</code> must be exactly
          {' '}<code className="bg-zinc-800 px-1 rounded">azure_ad</code>. The secret is stored as a
          Key Vault reference, so it never enters the database.
        </p>
      </div>
    </div>
  );
}

function SetupGuideTab({ tenant }) {
  const [copiedText, setCopiedText] = useState('');
  // Neither value can be derived in the browser when the panel is served
  // through a front door, so they are asked for once and remembered.
  const [appServiceHost, setAppServiceHost] = useState(() => detectAppServiceHost());
  const [verificationId, setVerificationId] = useState(() => detectVerificationId());
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const renderCopyButton = (text, label) => (
    <button
      onClick={() => copyToClipboard(text, label)}
      className="ml-2 p-1 text-zinc-500 hover:text-white transition-colors"
      title="Copy to clipboard"
    >
      {copiedText === label ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );

  const guideRedirectUris = redirectUris(tenantSignInHosts(tenant, TENANT_FALLBACK_DOMAIN));
  const guideDnsRecords = dnsRecords(tenant?.custom_domain, { appServiceHost }, verificationId);
  // Azure names a slot host <app>-<slot>.azurewebsites.net, so this is derivable
  // once the production hostname is known - but only then.
  const stagingUrl = appServiceHost
    ? `https://${appServiceHost.replace(/\.azurewebsites\.net$/, '-staging.azurewebsites.net')}`
    : '';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-zinc-800 pb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <HelpCircle className="h-6 w-6 text-navigate-gold" />
          Tenant Setup &amp; Deployment Guide
        </h2>
        <p className="text-zinc-400 mt-2">
          Complete guide for creating tenants, configuring staging environments, and deploying to production.
        </p>
      </div>

      {/* Values for the tenant currently selected, not examples */}
      <TenantSetupValues
        tenant={tenant}
        appServiceHost={appServiceHost}
        setAppServiceHost={setAppServiceHost}
        verificationId={verificationId}
        setVerificationId={setVerificationId}
        renderCopyButton={renderCopyButton}
      />

      {/* Step 1: Create Tenant */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">1</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Create a New Tenant</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Click the <strong className="text-navigate-gold">&quot;New Tenant&quot;</strong> button and fill in the required fields:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Organization Name</p>
                    <p className="text-sm text-zinc-400">The display name for the tenant (e.g., &quot;ACME Financial Services&quot;)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Subdomain (Slug) OR Custom Domain</p>
                    <p className="text-sm text-zinc-400">
                      <strong>Option A:</strong> Use a subdomain like <code className="bg-zinc-800 px-1 rounded">acme.cerebro.co.za</code><br/>
                      <strong>Option B:</strong> Use client&apos;s custom domain like <code className="bg-zinc-800 px-1 rounded">learning.acme.com</code>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Max Users &amp; Contact Details</p>
                    <p className="text-sm text-zinc-400">Set user limits and provide administrator contact information</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Configure Microsoft SSO */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">2</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Configure Microsoft Entra ID SSO</h3>
            <div className="space-y-4 text-zinc-300">
              <p>For enterprise users to login via Microsoft, configure Entra ID in <strong>Azure Portal</strong>:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-amber-400">Required Azure App Registration Settings:</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                    <span className="text-zinc-400">Client ID:</span>
                    <code className="text-white">ENTRA_CLIENT_ID</code>
                    {renderCopyButton("ENTRA_CLIENT_ID", "client-id")}
                  </div>
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                    <span className="text-zinc-400">Client Secret:</span>
                    <code className="text-white">ENTRA_CLIENT_SECRET</code>
                    {renderCopyButton("ENTRA_CLIENT_SECRET", "client-secret")}
                  </div>
                  {/* Derived per tenant. The fixed example that used to sit
                      here carried a copy button, so an administrator could paste
                      a URI that could never work into a client's registration. */}
                  {guideRedirectUris.length > 0 ? (
                    guideRedirectUris.map((uri, i) => (
                      <div key={uri} className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                        <span className="text-zinc-400 whitespace-nowrap">
                          {i === 0 ? 'Redirect URI:' : 'Also add:'}
                        </span>
                        <code className="text-white break-all ml-3">{uri}</code>
                        {renderCopyButton(uri, `redirect-uri-${i}`)}
                      </div>
                    ))
                  ) : (
                    <div className="bg-zinc-800 px-3 py-2 rounded text-sm text-zinc-500">
                      Redirect URI: select a tenant above to see the exact value to register.
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-zinc-500 mt-2">
                  Add these as environment variables in Azure App Service → Configuration → Application settings
                </p>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <strong>Note:</strong> Each enterprise tenant needs their Entra Tenant ID configured in the tenant settings
                  to enable domain-based SSO routing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Staging Environment */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">3</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Set Up Staging Environment</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Staging allows you to test changes before affecting production users.</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-4">
                <div>
                  <p className="font-medium text-white mb-2">A. Create Staging Slot in Azure Portal:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                    <li>Navigate to <strong>App Service</strong> → Your app</li>
                    <li>Go to <strong>Deployment → Deployment slots</strong></li>
                    <li>Click <strong>+ Add Slot</strong></li>
                    <li>Name it <code className="bg-zinc-800 px-1 rounded">staging</code></li>
                    <li>Clone settings from production (optional)</li>
                  </ol>
                </div>
                
                <div>
                  <p className="font-medium text-white mb-2">B. Configure Staging Environment Variables:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Environment:</span>
                      <code className="text-white">ENVIRONMENT=staging</code>
                      {renderCopyButton("ENVIRONMENT=staging", "env-staging")}
                    </div>
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Database:</span>
                      <code className="text-white">DB_NAME=cerebro_staging</code>
                      {renderCopyButton("DB_NAME=cerebro_staging", "db-staging")}
                    </div>
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Admin Access:</span>
                      <code className="text-white">ADMIN_ACCESS_TOKEN=&lt;your-token&gt;</code>
                      {renderCopyButton("ADMIN_ACCESS_TOKEN=", "admin-token")}
                    </div>
                  </div>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-sm text-amber-300">
                    <strong>Important:</strong> Use a separate database (cerebro_staging) for staging to avoid affecting production data.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-medium text-white mb-2">Staging URL:</p>
                {stagingUrl ? (
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded text-sm">
                    <code className="text-white break-all">{stagingUrl}</code>
                    {renderCopyButton(stagingUrl, 'staging-url')}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">
                    Set the App Service hostname above to see this. Slot naming varies per
                    deployment, so guessing it here would be a value that does not exist.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 4: Deploy to Staging */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">4</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Deploy to Staging</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Use GitHub Actions for safe, automated deployments:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Push changes to the <code className="bg-zinc-800 px-1 rounded">staging</code> branch on GitHub</li>
                  <li>The deployment starts automatically and goes to the staging slot</li>
                  <li>Watch it under <strong>GitHub → Actions → &quot;Deploy to Azure App Service&quot;</strong></li>
                  <li>To deploy manually instead, use <strong>&quot;Run workflow&quot;</strong> with <strong>deploy_target: staging</strong></li>
                </ol>
                
                <div className="flex items-center gap-2 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                  <p className="text-sm text-red-300">
                    The branch decides the environment. A push to <strong>staging</strong> goes to the staging slot; a push to <strong>main</strong> deploys straight to <strong>production</strong>. The deploy_target option is only used for manual runs — it is ignored on a push.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-medium text-white mb-2">After deployment, verify:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Health check passes at <code className="bg-zinc-800 px-1 rounded">/api/health</code></li>
                  <li>Admin login works with your access token</li>
                  <li>Tenant branding displays correctly</li>
                  <li>Microsoft SSO redirects properly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 5: Promote to Production */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">5</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Promote to Production</h3>
            <div className="space-y-4 text-zinc-300">
              <p>After validating staging, promote to production:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="font-medium text-white">Option A: Slot Swap (Recommended - Zero Downtime)</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to Azure Portal → App Service → Deployment slots</li>
                  <li>Click <strong>&quot;Swap&quot;</strong></li>
                  <li>Source: <code className="bg-zinc-800 px-1 rounded">staging</code> → Target: <code className="bg-zinc-800 px-1 rounded">production</code></li>
                  <li>Review changes and confirm</li>
                </ol>
                
                <div className="border-t border-zinc-700 my-4"></div>
                
                <p className="font-medium text-white">Option B: Direct Deploy via GitHub Actions</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to GitHub → Actions → &quot;Deploy to Azure App Service&quot;</li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong></li>
                  <li>Select <strong>deploy_target: production</strong></li>
                  <li>Type <strong className="text-red-400">DEPLOY</strong> in the confirmation field</li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong></li>
                </ol>
                
                <div className="flex items-center gap-2 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  <p className="text-sm text-red-300">
                    Production requires typing <strong>&quot;DEPLOY&quot;</strong> to confirm. This prevents accidental deployments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 6: Custom Domain Setup */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">6</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Configure Custom Domain (Optional)</h3>
            <div className="space-y-4 text-zinc-300">
              {tenant?.custom_domain ? (
                <p>
                  DNS records for <code className="bg-zinc-800 px-1 rounded">{tenant.custom_domain}</code>,
                  the domain stored on <strong className="text-white">{tenant.name}</strong>:
                </p>
              ) : (
                <p>Select a tenant with a custom domain to see the exact records it needs.</p>
              )}

              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="font-medium text-white">DNS records to create:</p>
                {guideDnsRecords.length > 0 ? (
                  guideDnsRecords.map((record) => (
                    <div key={`${record.type}-${record.name}`} className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded text-sm font-mono gap-3">
                      <span className="text-zinc-400 w-16 flex-shrink-0">{record.type}</span>
                      <span className="text-white flex-shrink-0">{record.name}</span>
                      {record.value ? (
                        <>
                          <span className="text-green-400 break-all flex-1 text-right">{record.value}</span>
                          {renderCopyButton(record.value, `dns-${record.type}-${record.name}`)}
                        </>
                      ) : (
                        // Never offer a copy button on a value we do not have -
                        // that is what made the old guide dangerous.
                        <span className="text-amber-500/80 flex-1 text-right">{record.hint}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500">No custom domain stored on this tenant.</p>
                )}

                <p className="text-sm text-zinc-400 mt-2">
                  Both records are required. Azure validation fails without the
                  {' '}<code className="bg-zinc-800 px-1 rounded">asuid</code> TXT record, which is
                  the most common point of failure.
                </p>

                <p className="font-medium text-white mt-4">Azure App Service Configuration:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to App Service → Custom domains</li>
                  <li>Copy the <strong>Custom Domain Verification ID</strong> and give it to the client for the TXT record</li>
                  <li>Click <strong>&quot;+ Add custom domain&quot;</strong></li>
                  <li>Enter the domain and validate</li>
                  <li>Add SSL binding (use App Service Managed Certificate for free SSL)</li>
                </ol>
                
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mt-3">
                  <p className="text-sm text-blue-300">
                    <strong>Tip:</strong> The tenant&apos;s custom domain must match what&apos;s configured in their tenant settings for proper routing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Launch Checklist */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-green-500/30">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Tenant Launch Checklist</h3>
            <p className="text-zinc-400 mb-4">Complete these steps before launching a tenant to users:</p>
            
            <div className="space-y-4">
              {/* Phase 1: Basic Setup */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-amber-400 mb-3">Phase 1: Basic Setup (Required)</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">1</div>
                    <span className="text-zinc-300">Create tenant with name and slug/domain</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">2</div>
                    <span className="text-zinc-300">Configure branding (logo, colors, login background)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">3</div>
                    <span className="text-zinc-300">Enable/disable features for the tenant</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">4</div>
                    <span className="text-zinc-300">Create at least one course in the Content tab</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">5</div>
                    <span className="text-zinc-300">Publish courses to the tenant</span>
                  </div>
                </div>
              </div>

              {/* Phase 2: Authentication */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-blue-400 mb-3">Phase 2: User Authentication</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">6</div>
                    <span className="text-zinc-300">Configure Microsoft Entra ID SSO in Azure Portal</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">7</div>
                    <span className="text-zinc-300">Set tenant&apos;s Entra Tenant ID in tenant settings</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">8</div>
                    <span className="text-zinc-300">Test SSO login with a user from the tenant&apos;s organization</span>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300">
                  <strong>When is SSO needed?</strong> SSO is optional. Email and password sign-in works without it, so a tenant can launch and users can log in before SSO is configured. Configure SSO when the organisation wants staff to sign in with their existing Microsoft accounts.
                </div>
              </div>

              {/* Phase 3: Custom Domain */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-purple-400 mb-3">Phase 3: Custom Domain (Optional)</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">9</div>
                    <span className="text-zinc-300">Client creates CNAME record pointing to your app</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">10</div>
                    <span className="text-zinc-300">Add custom domain in Azure App Service</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">11</div>
                    <span className="text-zinc-300">Configure SSL certificate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">12</div>
                    <span className="text-zinc-300">Update tenant settings with custom domain</span>
                  </div>
                </div>
              </div>

              {/* Ready Indicators */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="font-medium text-green-400 mb-2">How to know when a tenant is ready:</p>
                <ul className="space-y-1 text-sm text-zinc-300 list-disc list-inside ml-2">
                  <li>Tenant appears in the Tenants list with &quot;Active&quot; status</li>
                  <li>At least one course is published to the tenant</li>
                  <li>Branding is configured (logo displays on login page)</li>
                  <li>SSO is configured and tested (for production)</li>
                  <li>Preview/Launch button shows the tenant&apos;s branded login page</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Reference */}
      <div className="bg-gradient-to-r from-navigate-gold/10 to-transparent rounded-xl p-6 border border-navigate-gold/30">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-navigate-gold" />
          Quick Reference - Required Environment Variables
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">Authentication</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">ADMIN_ACCESS_TOKEN=</span><span className="text-white">&lt;32-char-token&gt;</span></p>
              <p><span className="text-zinc-500">JWT_SECRET=</span><span className="text-white">&lt;secure-secret&gt;</span></p>
              <p><span className="text-zinc-500">ENTRA_TENANT_ID=</span><span className="text-white">&lt;guid&gt;</span></p>
              <p><span className="text-zinc-500">ENTRA_CLIENT_ID=</span><span className="text-white">&lt;guid&gt;</span></p>
              <p><span className="text-zinc-500">ENTRA_CLIENT_SECRET=</span><span className="text-white">&lt;secret or keyvault:name&gt;</span></p>
            </div>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">Database &amp; Storage</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">MONGO_URL=</span><span className="text-white">&lt;cosmos-connection&gt;</span></p>
              <p><span className="text-zinc-500">DB_NAME=</span><span className="text-white">cerebro_production</span></p>
              <p><span className="text-zinc-500">AZURE_STORAGE_CONNECTION_STRING=</span><span className="text-white">&lt;blob-conn&gt;</span></p>
              <p><span className="text-zinc-500">AZURE_STORAGE_ACCOUNT_NAME=</span><span className="text-white">&lt;account&gt;</span></p>
            </div>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">AI Providers</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">LLM_PROVIDER=</span><span className="text-white">anthropic</span></p>
              <p><span className="text-zinc-500">ANTHROPIC_API_KEY=</span><span className="text-white">&lt;key&gt;</span></p>
              <p><span className="text-zinc-500">IMAGE_PROVIDER=</span><span className="text-white">azure_openai | openai</span></p>
            </div>
            <p className="text-xs text-zinc-500 mt-2">Image generation needs its own provider. Anthropic does not generate images, so leaving IMAGE_PROVIDER unconfigured makes every thumbnail generation fail.</p>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">Application</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">ENVIRONMENT=</span><span className="text-white">production | staging</span></p>
              <p><span className="text-zinc-500">CORS_ORIGINS=</span><span className="text-white">&lt;comma-separated&gt;</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}